import { useEffect, useRef, useCallback } from 'react';

/**
 * Saves and restores window.scrollY for a given route key across refreshes.
 *
 * Usage:
 *   const restoreScroll = useScrollRestoration('/my-route');
 *   useEffect(() => { if (!loading) restoreScroll(); }, [loading, restoreScroll]);
 *
 * Call restoreScroll() exactly once, after the page content has been rendered
 * (e.g. when loading flips to false for the first time). The hook is idempotent —
 * calling restoreScroll() multiple times only applies the scroll once.
 */
export function useScrollRestoration(routeKey) {
  const storageKey = `scroll:${routeKey}`;
  const restoredRef = useRef(false);

  // Persist scroll position on every scroll event (rAF-debounced to avoid thrashing)
  useEffect(() => {
    let rafId;
    const save = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        sessionStorage.setItem(storageKey, String(Math.round(window.scrollY)));
      });
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => {
      window.removeEventListener('scroll', save);
      cancelAnimationFrame(rafId);
    };
  }, [storageKey]);

  // Reset the guard on every mount so navigating away and back re-enables restore
  useEffect(() => {
    restoredRef.current = false;
  }, []);

  const restore = useCallback(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const saved = sessionStorage.getItem(storageKey);
    const y = saved ? Number(saved) : 0;
    if (!y) return;
    // requestAnimationFrame ensures the DOM has finished painting before scrolling
    requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'instant' }));
  }, [storageKey]);

  return restore;
}
